import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton, Card, CardContent, LinearProgress, Fade } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getCaptchaToken } from '../services/turnstile';
import { apiCall } from '../api/config';

const LOADING_STEPS = [
  { at: 0, text: 'Analisi della foto in corso...' },
  { at: 8, text: 'Rilevamento del tono della pelle...' },
  { at: 18, text: 'Analisi del sottotono (caldo/freddo)...' },
  { at: 30, text: 'Valutazione del colore degli occhi e capelli...' },
  { at: 42, text: 'Calcolo del contrasto e del croma...' },
  { at: 55, text: 'Determinazione della sotto-stagione...' },
  { at: 68, text: 'Verifica coerenza palette 12 sotto-stagioni...' },
  { at: 80, text: 'Generazione della palette personalizzata...' },
  { at: 90, text: 'Validazione colori e consigli di stile...' },
  { at: 95, text: 'Preparazione dei risultati...' },
];

const CURIOSITA = [
  'Ognuno di noi ha dei colori che lo fanno sembrare subito più riposato e luminoso. L\'armocromia serve proprio a trovarli!',
  'La tua pelle ha un "sottotono" che non cambia mai, nemmeno con l\'abbronzatura. I colori che ti stanno bene restano sempre gli stessi.',
  'Trucco veloce: guarda le vene del polso. Se tendono al verde hai un sottotono caldo, se tendono al blu hai un sottotono freddo.',
  'Indossare i colori giusti illumina il viso, riduce le occhiaie e rende la pelle più uniforme. Quelli sbagliati fanno l\'effetto opposto!',
  'L\'armocromia non vale solo per i vestiti: anche il trucco, i gioielli, le montature degli occhiali e il colore dei capelli seguono le stesse regole.',
  'Marilyn Monroe stava benissimo con i colori caldi e vivaci. Audrey Hepburn invece con il nero e il bianco puro. Ognuno ha la sua palette!',
  'Piccola regola per i gioielli: se hai un sottotono caldo, l\'oro giallo ti valorizza. Se ce l\'hai freddo, meglio l\'argento o il platino.',
  'Più della metà delle persone sceglie istintivamente i colori opposti a quelli che li valorizzano. Per questo un\'analisi fa davvero la differenza.',
  'In Giappone e Corea l\'analisi dei colori personali è diventata una mania: ci sono studi dedicati in ogni quartiere delle grandi città!',
  'L\'idea di abbinare i colori alle persone nasce negli anni \'30, quando un artista svizzero notò che i suoi studenti dipingevano meglio usando i colori in armonia col proprio aspetto.',
  'Esistono ben 12 varianti di palette personalizzate, non solo le 4 stagioni base. Tra poco scoprirai qual è la tua!',
  'Un colore "sbagliato" non è brutto in sé: semplicemente non è in armonia con le tue tonalità naturali. Quello giusto ti fa brillare.',
];

const CONSIGLI_PER_STAGIONE = {
  'Primavera': [
    { icon: '🖤', title: 'Il nero', text: 'Evita il nero puro vicino al viso: ti farebbe sembrare stanca. Sostituiscilo con il marrone cioccolato o il blu notte.' },
    { icon: '💄', title: 'Il tuo rossetto', text: 'Il corallo è il tuo portafortuna: illumina il viso in un attimo e ti dona una luminosità naturale.' },
    { icon: '✨', title: 'Abiti da sera', text: 'Per le occasioni importanti, osa con un abito verde acqua o turchese caldo: sono colori che ti fanno brillare.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Preferisci i lavaggi chiari e caldi: il denim scuro e freddo ti spegne.' },
    { icon: '🤍', title: 'Il tuo bianco', text: 'Il bianco panna e l\'avorio sono molto più adatti a te del bianco ottico: riscaldano il viso invece di appiattirlo.' },
    { icon: '👁️', title: 'Make-up occhi', text: 'Punta su ombretti color pesca, bronzo caldo e verde oliva. Gli occhi si accendono subito.' },
    { icon: '💇', title: 'Colore capelli', text: 'Ti stanno bene con riflessi dorati, ramati o miele. Evita il cenere o il freddo.' },
    { icon: '👜', title: 'Borsa must-have', text: 'Una borsa cammello o cuoio caldo è un investimento sicuro: si abbina a quasi tutto il tuo guardaroba.' },
    { icon: '🌸', title: 'Il tuo rosa', text: 'Il rosa confetto e il salmone ti valorizzano molto più del rosa shocking freddo.' },
    { icon: '👓', title: 'Occhiali', text: 'Preferisci montature color tartaruga, corno o oro rispetto a quelle nere o argento.' },
    { icon: '🌞', title: 'Il giallo sole', text: 'È uno dei tuoi colori migliori, anche se ti spaventa: provalo almeno con un accessorio.' },
    { icon: '💼', title: 'Look ufficio', text: 'Il blu navy ti sta bene ma deve essere un navy caldo, non freddo.' },
    { icon: '🌆', title: 'Outfit da sera', text: 'Un abito color terracotta o arancio bruciato ti regala un look sofisticato e solare.' },
    { icon: '🎨', title: 'Colori vivaci', text: 'Non temerli: il tuo incarnato li supporta bene. Osa con corallo, turchese, verde lime.' },
  ],
  'Estate': [
    { icon: '🖤', title: 'Il nero', text: 'Ti appesantisce: sostituiscilo con il blu notte, il grigio antracite o il viola scuro.' },
    { icon: '💄', title: 'Il tuo rossetto', text: 'Un rosa freddo o un lilla delicato. Evita i toni arancio.' },
    { icon: '✨', title: 'Abiti da sera', text: 'Un abito color lavanda o malva ti regala un\'aura raffinata ed elegante.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Preferisci lavaggi freddi e chiari: evita i denim con tonalità gialle o calde.' },
    { icon: '🤍', title: 'Il tuo bianco', text: 'Il bianco latte o il ghiaccio, non il bianco caldo o avorio.' },
    { icon: '👁️', title: 'Make-up occhi', text: 'Ombretti freddi: grigio fumo, lavanda, blu polvere, rosa antico.' },
    { icon: '💇', title: 'Colore capelli', text: 'Riflessi cenere, platino o marrone freddo. Evita i toni ramati.' },
    { icon: '👜', title: 'Borsa must-have', text: 'Una borsa grigio perla o tortora freddo si abbina a tutta la tua palette.' },
    { icon: '🌸', title: 'Il tuo rosa', text: 'Rosa cipria e rosa polvere sono perfetti, non quelli caldi e saturi.' },
    { icon: '👓', title: 'Occhiali', text: 'Preferisci montature grigie, blu o argento. Evita il tartaruga caldo.' },
    { icon: '🎭', title: 'Contrasti', text: 'Il contrasto alto ti appesantisce. Preferisci look tono-su-tono o transizioni morbide.' },
    { icon: '💼', title: 'Look ufficio', text: 'Il blu acciaio e il blu polvere sono più valorizzanti del navy classico.' },
    { icon: '🌿', title: 'Verde salvia', text: 'Verde salvia e verde menta freddo sono scelte eccellenti per l\'estate.' },
    { icon: '🌆', title: 'Outfit da sera', text: 'Un abito color prugna chiaro o blu polvere ti dona un\'eleganza raffinata.' },
  ],
  'Autunno': [
    { icon: '🖤', title: 'Il nero', text: 'Non è il tuo colore: sostituiscilo con marrone cioccolato fondente o verde bosco.' },
    { icon: '💄', title: 'Il tuo rossetto', text: 'Un marrone caldo, un mattone o un terracotta intenso ti valorizza molto.' },
    { icon: '✨', title: 'Abiti da sera', text: 'Osa con bordeaux, ruggine o verde foresta: colori profondi e regali.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Lavaggi medi e caldi, con possibili sfumature rossastre.' },
    { icon: '🤍', title: 'Il tuo bianco', text: 'L\'avorio e il beige caldo. Il bianco ottico ti fa sembrare grigia.' },
    { icon: '👁️', title: 'Make-up occhi', text: 'Ombretti color oro, bronzo, verde oliva, marrone caldo, ramato.' },
    { icon: '💇', title: 'Colore capelli', text: 'Riflessi ramati, caramello, castano caldo o rosso esplodono su di te.' },
    { icon: '👜', title: 'Borsa must-have', text: 'Una borsa in cuoio naturale o cognac: invecchia bene e ti sta sempre bene.' },
    { icon: '🌸', title: 'Il tuo rosa', text: 'Rosa antico, pesca tenue, terracotta chiara: sempre tonalità calme e calde.' },
    { icon: '👓', title: 'Occhiali', text: 'Montature tartaruga, cuoio, oro antico. Evita il nero e l\'argento.' },
    { icon: '🧥', title: 'Monocromatico', text: 'Outfit monocromatici in toni caldi (cammello + moro + cuoio) = eleganza.' },
    { icon: '💼', title: 'Look ufficio', text: 'Sostituisci il classico blu navy con il marrone scuro o il verde petrolio.' },
    { icon: '🌿', title: 'Verdi', text: 'Verde oliva e kaki scuro sono tra i tuoi colori più valorizzanti.' },
    { icon: '🌆', title: 'Outfit da sera', text: 'Un abito color arancio bruciato o rosso mattone è sensazionale.' },
  ],
  'Inverno': [
    { icon: '🖤', title: 'Il nero', text: 'È uno dei tuoi colori base perfetti: lo porti come pochi altri. Abbinalo a bianco ottico o rosso vivo.' },
    { icon: '💄', title: 'Il tuo rossetto', text: 'Un rosso cremisi, un fucsia freddo o una prugna scura intensa.' },
    { icon: '✨', title: 'Abiti da sera', text: 'Blu royal, verde smeraldo o viola saturo: sei spettacolare.' },
    { icon: '👖', title: 'I jeans giusti', text: 'Lavaggi scuri e freddi. Evita i denim chiari e caldi.' },
    { icon: '🤍', title: 'Il tuo bianco', text: 'Il bianco ottico è uno dei tuoi migliori amici: ti illumina il viso.' },
    { icon: '👁️', title: 'Make-up occhi', text: 'Ombretti neri, grigio fumo, blu notte, viola intenso, argento brillante.' },
    { icon: '💇', title: 'Colore capelli', text: 'Nero corvino, castano scuro, platino, cenere: toni scurissimi e freddi.' },
    { icon: '👜', title: 'Borsa must-have', text: 'Una borsa nera è un investimento eterno: funziona con il 90% dei tuoi outfit.' },
    { icon: '🌸', title: 'Il tuo rosa', text: 'Solo quelli freddi e saturi: fucsia vivo o magenta.' },
    { icon: '👓', title: 'Occhiali', text: 'Montature nere, argento o trasparenti. Evita il tartaruga caldo.' },
    { icon: '🎭', title: 'Contrasti', text: 'Il contrasto alto è la tua forza: abbina colori scuri a chiari per effetti drammatici.' },
    { icon: '💼', title: 'Look ufficio', text: 'Blu notte saturo > navy classico. Abbinalo al bianco ottico.' },
    { icon: '🌿', title: 'Verdi', text: 'Verde smeraldo e teal profondo: tra i colori che ti donano di più.' },
    { icon: '🌆', title: 'Outfit da sera', text: 'Un abito color rubino, zaffiro o nero con accessori argentati è perfetto.' },
  ]
};

// Mescola le curiosità in ordine casuale (Fisher-Yates shuffle)
function shuffleArray(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function ArmocromiaResults() {
  const navigate = useNavigate();
  const [userPhoto, setUserPhoto] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState(LOADING_STEPS[0].text);
  const [curiositaText, setCuriositaText] = useState('');
  const [curiositaVisible, setCuriositaVisible] = useState(true);
  const [consiglioIdx, setConsiglioIdx] = useState(0);
  const [consiglioVisible, setConsiglioVisible] = useState(true);
  const [consiglioDirection, setConsiglioDirection] = useState('next');
  const progressInterval = useRef(null);
  const curiositaInterval = useRef(null);
  const consiglioInterval = useRef(null);
  const shuffledCuriosita = useRef(shuffleArray(CURIOSITA));
  const shuffledConsigli = useRef([]);
  const curiositaIdxRef = useRef(0);

  const goToCuriosita = (direction) => {
    if (curiositaInterval.current) {
      clearInterval(curiositaInterval.current);
    }
    setCuriositaVisible(false);
    setTimeout(() => {
      const len = shuffledCuriosita.current.length;
      curiositaIdxRef.current = (curiositaIdxRef.current + direction + len) % len;
      setCuriositaText(shuffledCuriosita.current[curiositaIdxRef.current]);
      setCuriositaVisible(true);
    }, 300);
    curiositaInterval.current = setInterval(() => {
      setCuriositaVisible(false);
      setTimeout(() => {
        curiositaIdxRef.current = (curiositaIdxRef.current + 1) % shuffledCuriosita.current.length;
        setCuriositaText(shuffledCuriosita.current[curiositaIdxRef.current]);
        setCuriositaVisible(true);
      }, 400);
    }, 8000);
  };

  const startProgressBar = useCallback(() => {
    const duration = 30000;
    const intervalMs = 300;
    const maxProgress = 95;
    let elapsed = 0;

    progressInterval.current = setInterval(() => {
      elapsed += intervalMs;
      const raw = (elapsed / duration) * maxProgress;
      const eased = maxProgress * (1 - Math.pow(1 - Math.min(raw / maxProgress, 1), 2.5));
      const currentProgress = Math.min(eased, maxProgress);

      setProgress(currentProgress);

      for (let i = LOADING_STEPS.length - 1; i >= 0; i--) {
        if (currentProgress >= LOADING_STEPS[i].at) {
          setLoadingText(LOADING_STEPS[i].text);
          break;
        }
      }
    }, intervalMs);

    // Curiosità: ordine casuale, cambia ogni 8s con fade in/out
    shuffledCuriosita.current = shuffleArray(CURIOSITA);
    curiositaIdxRef.current = 0;
    setCuriositaText(shuffledCuriosita.current[0]);
    setCuriositaVisible(true);
    curiositaInterval.current = setInterval(() => {
      setCuriositaVisible(false);
      setTimeout(() => {
        curiositaIdxRef.current = (curiositaIdxRef.current + 1) % shuffledCuriosita.current.length;
        setCuriositaText(shuffledCuriosita.current[curiositaIdxRef.current]);
        setCuriositaVisible(true);
      }, 400);
    }, 8000);
  }, []);

  const stopProgressBar = useCallback(() => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
    if (curiositaInterval.current) {
      clearInterval(curiositaInterval.current);
      curiositaInterval.current = null;
    }
    setProgress(100);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressInterval.current) clearInterval(progressInterval.current);
      if (curiositaInterval.current) clearInterval(curiositaInterval.current);
      if (consiglioInterval.current) clearInterval(consiglioInterval.current);
    };
  }, []);

  // Start consigli slider when result arrives
  useEffect(() => {
    if (!result?.season) return;
    const pool = CONSIGLI_PER_STAGIONE[result.season] || [];
    if (pool.length === 0) return;

    const extras = [];
    if (result.tips) {
      extras.push({ icon: '💡', title: 'Consiglio dell\'AI', text: result.tips });
    }
    shuffledConsigli.current = [...extras, ...shuffleArray(pool)];
    setConsiglioIdx(0);
    setConsiglioVisible(true);

    if (consiglioInterval.current) clearInterval(consiglioInterval.current);
    let idx = 0;
    consiglioInterval.current = setInterval(() => {
      setConsiglioDirection('next');
      setConsiglioVisible(false);
      setTimeout(() => {
        idx = (idx + 1) % shuffledConsigli.current.length;
        setConsiglioIdx(idx);
        setConsiglioVisible(true);
      }, 280);
    }, 9000);

    return () => {
      if (consiglioInterval.current) clearInterval(consiglioInterval.current);
    };
  }, [result?.season, result?.tips]);

  const goToConsiglio = (direction) => {
    if (consiglioInterval.current) clearInterval(consiglioInterval.current);
    setConsiglioDirection(direction > 0 ? 'next' : 'prev');
    setConsiglioVisible(false);
    setTimeout(() => {
      const len = shuffledConsigli.current.length;
      if (len === 0) return;
      const newIdx = (consiglioIdx + direction + len) % len;
      setConsiglioIdx(newIdx);
      setConsiglioVisible(true);
    }, 250);
    let idx = (consiglioIdx + direction + shuffledConsigli.current.length) % shuffledConsigli.current.length;
    consiglioInterval.current = setInterval(() => {
      setConsiglioDirection('next');
      setConsiglioVisible(false);
      setTimeout(() => {
        idx = (idx + 1) % shuffledConsigli.current.length;
        setConsiglioIdx(idx);
        setConsiglioVisible(true);
      }, 280);
    }, 9000);
  };

  const analyzeSeason = useCallback(async (photo) => {
    try {
      setLoading(true);
      setError(null);
      startProgressBar();

      // Read email for server-side sending
      let userEmail = null;
      try {
        const contactData = JSON.parse(localStorage.getItem('userContact') || 'null');
        if (contactData?.type === 'email' && contactData.value) {
          userEmail = contactData.value;
        }
      } catch (e) { /* ignore */ }

      const captchaToken = await getCaptchaToken();
      const body = { photo, captchaToken };
      if (userEmail) body.email = userEmail;

      const data = await apiCall('/analyze-armocromia', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      stopProgressBar();
      setResult(data);

      if (data.emailSent) {
        setEmailSent(true);
      }
    } catch (err) {
      console.error('Errore analisi armocromia:', err);
      stopProgressBar();
      setError(err.message || 'Non è stato possibile analizzare la foto. Riprova.');
    } finally {
      setLoading(false);
    }
  }, [startProgressBar, stopProgressBar]);

  useEffect(() => {
    const photo = localStorage.getItem('userPhoto');
    if (photo) {
      setUserPhoto(photo);
      analyzeSeason(photo);
    } else {
      setLoading(false);
      setError('Nessuna foto trovata. Scatta una foto prima.');
    }
  }, [analyzeSeason]);

  const seasonGradients = {
    'Primavera': 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
    'Estate': 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    'Autunno': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'Inverno': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  };

  const isDark = loading; // dark theme during loading, light for results

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        background: isDark
          ? 'linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)'
          : '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        transition: 'background 0.6s ease'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: isDark ? 'rgba(255,255,255,0.08)' : '#ffffff',
          backdropFilter: isDark ? 'blur(10px)' : 'none',
          boxShadow: isDark ? 'none' : '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <IconButton
          onClick={() => navigate('/store-camera')}
          sx={{
            color: isDark ? 'white' : 'rgb(51, 51, 51)',
            backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'transparent',
            width: '44px',
            height: '44px'
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography
          sx={{
            fontSize: '18px',
            color: isDark ? 'white' : 'rgb(51, 51, 51)',
            fontWeight: 700
          }}
        >
          {loading ? 'Analisi in corso' : 'I Tuoi Risultati'}
        </Typography>

        <Box sx={{ width: '44px' }} />
      </Box>

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          padding: { xs: '16px', sm: '24px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: loading ? 'center' : 'flex-start',
          gap: loading ? 2 : 3
        }}
      >
        {/* User photo (solo quando non in loading) */}
        {userPhoto && !loading && (
          <Box
            sx={{
              width: { xs: '140px', sm: '180px' },
              height: { xs: '140px', sm: '180px' },
              borderRadius: '50%',
              overflow: 'hidden',
              border: '4px solid #ffffff',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
          >
            <img src={userPhoto} alt="User" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 2, sm: 2.5 } }}>

            {/* Miniatura foto con animazione scansione */}
            {userPhoto && (
              <Box sx={{ position: 'relative', width: { xs: 100, sm: 130 }, height: { xs: 100, sm: 130 } }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid rgba(102,126,234,0.4)',
                    boxShadow: '0 0 40px rgba(102,126,234,0.3)'
                  }}
                >
                  <img src={userPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                {/* Linea di scansione */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '10%',
                    width: '80%',
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, #667eea, #c8a7ff, transparent)',
                    borderRadius: '2px',
                    boxShadow: '0 0 12px rgba(102,126,234,0.7)',
                    animation: 'scanLine 2.5s ease-in-out infinite',
                    '@keyframes scanLine': {
                      '0%': { top: '5%', opacity: 0 },
                      '10%': { opacity: 1 },
                      '90%': { opacity: 1 },
                      '100%': { top: '95%', opacity: 0 }
                    }
                  }}
                />
                {/* Glow pulsante */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -4,
                    left: -4,
                    right: -4,
                    bottom: -4,
                    borderRadius: '50%',
                    border: '2px solid rgba(102,126,234,0.3)',
                    animation: 'glowPulse 2s ease-in-out infinite',
                    '@keyframes glowPulse': {
                      '0%, 100%': { boxShadow: '0 0 10px rgba(102,126,234,0.15)', transform: 'scale(1)' },
                      '50%': { boxShadow: '0 0 30px rgba(102,126,234,0.5)', transform: 'scale(1.04)' }
                    }
                  }}
                />
              </Box>
            )}

            {/* Progress bar + percentage */}
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      background: 'linear-gradient(90deg, #667eea, #c8a7ff)',
                      transition: 'transform 0.3s ease'
                    }
                  }}
                />
                <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontWeight: 700, minWidth: '36px', textAlign: 'right' }}>
                  {Math.round(progress)}%
                </Typography>
              </Box>

              {/* Step text */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: 'center'
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#c8a7ff',
                    flexShrink: 0,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.4, transform: 'scale(0.8)' }
                    }
                  }}
                />
                <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
                  {loadingText}
                </Typography>
              </Box>
            </Box>

            {/* Curiosità */}
            <Box
              sx={{
                width: '100%',
                p: { xs: 2, sm: 2.5 },
                backgroundColor: 'rgba(255,255,255,0.06)',
                borderRadius: '16px',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                minHeight: '80px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Fade in={curiositaVisible} timeout={400}>
                <Box>
                  <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#c8a7ff', textTransform: 'uppercase', letterSpacing: '1.5px', mb: 0.5 }}>
                    Lo sapevi?
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
                    {curiositaText}
                  </Typography>
                </Box>
              </Fade>
            </Box>
            {/* Frecce navigazione */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <IconButton
                onClick={() => goToCuriosita(-1)}
                sx={{
                  color: 'rgba(255,255,255,0.4)',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  width: 40,
                  height: 40,
                  '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.12)' }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => goToCuriosita(1)}
                sx={{
                  color: 'rgba(255,255,255,0.4)',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  width: 40,
                  height: 40,
                  '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.12)' }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Card sx={{ width: '100%', maxWidth: '400px', borderRadius: '16px' }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Typography sx={{ fontSize: '16px', color: '#d32f2f', mb: 2 }}>
                {error}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/store-camera')}
                sx={{
                  backgroundColor: 'rgb(0, 0, 0)',
                  borderRadius: '12px',
                  textTransform: 'none'
                }}
              >
                Riprova
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {result && !loading && (
          <>
            {/* Email status */}
            {emailSent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#e8f5e9', borderRadius: '12px', px: 2, py: 1 }}>
                <CheckCircleIcon sx={{ fontSize: '20px', color: '#4caf50' }} />
                <Typography sx={{ fontSize: '13px', color: '#2e7d32', fontWeight: 500 }}>
                  Risultati inviati alla tua email!
                </Typography>
              </Box>
            )}

            {/* Season result */}
            <Card
              sx={{
                width: '100%',
                maxWidth: '400px',
                borderRadius: '16px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  background: seasonGradients[result.season] || seasonGradients['Inverno'],
                  padding: '20px',
                  textAlign: 'center'
                }}
              >
                <Typography sx={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', fontWeight: 500, mb: 0.5 }}>
                  La tua stagione armocromia è
                </Typography>
                <Typography sx={{ fontSize: '36px', fontWeight: 700, color: '#ffffff' }}>
                  {result.season}
                </Typography>
                {result.subSeason && (
                  <Typography sx={{ fontSize: '16px', color: 'rgba(255,255,255,0.85)', fontWeight: 500, mt: 0.5 }}>
                    {result.subSeason}
                  </Typography>
                )}
              </Box>
              <CardContent sx={{ p: 3 }}>
                {/* Caratteristiche rilevate */}
                {(result.skinTone || result.hairColor || result.eyeColor) && (
                  <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {result.skinTone && (
                      <Typography sx={{ fontSize: '13px', color: '#666' }}>
                        <strong>Pelle:</strong> {result.skinTone}
                      </Typography>
                    )}
                    {result.hairColor && (
                      <Typography sx={{ fontSize: '13px', color: '#666' }}>
                        <strong>Capelli:</strong> {result.hairColor}
                      </Typography>
                    )}
                    {result.eyeColor && (
                      <Typography sx={{ fontSize: '13px', color: '#666' }}>
                        <strong>Occhi:</strong> {result.eyeColor}
                      </Typography>
                    )}
                    {result.undertone && (
                      <Typography sx={{ fontSize: '13px', color: '#666' }}>
                        <strong>Sottotono:</strong> {result.undertone === 'warm' ? 'Caldo' : 'Freddo'} · <strong>Contrasto:</strong> {result.contrast === 'high' ? 'Alto' : result.contrast === 'low' ? 'Basso' : 'Medio'}
                      </Typography>
                    )}
                  </Box>
                )}
                <Typography sx={{ fontSize: '14px', color: 'rgb(102, 102, 102)', lineHeight: 1.6 }}>
                  {result.description}
                </Typography>
              </CardContent>
            </Card>


            {/* Consigli slider */}
            {shuffledConsigli.current.length > 0 && (() => {
              const current = shuffledConsigli.current[consiglioIdx] || {};
              const slideDir = consiglioDirection === 'next' ? '30px' : '-30px';
              return (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '400px',
                    p: 2.5,
                    background: 'linear-gradient(135deg, rgba(102,126,234,0.06), rgba(240,147,251,0.06))',
                    border: '1px solid rgba(102,126,234,0.15)',
                    borderRadius: '18px',
                    overflow: 'hidden'
                  }}
                >
                  <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#667eea', textTransform: 'uppercase', letterSpacing: '1.5px', mb: 1.5, textAlign: 'left' }}>
                    Consigli per te
                  </Typography>
                  <Fade in={consiglioVisible} timeout={300}>
                    <Box
                      key={consiglioIdx}
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                        minHeight: '60px',
                        animation: consiglioVisible ? `slideIn 0.3s ease-out` : 'none',
                        '@keyframes slideIn': {
                          from: { opacity: 0, transform: `translateX(${slideDir})` },
                          to: { opacity: 1, transform: 'translateX(0)' }
                        }
                      }}
                    >
                      <Box sx={{ fontSize: '24px', flexShrink: 0, mt: 0.25 }}>{current.icon}</Box>
                      <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#333', mb: 0.3 }}>
                          {current.title}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#666', lineHeight: 1.6 }}>
                          {current.text}
                        </Typography>
                      </Box>
                    </Box>
                  </Fade>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5 }}>
                    <IconButton
                      onClick={() => goToConsiglio(-1)}
                      size="small"
                      sx={{ color: '#aaa', backgroundColor: 'rgba(102,126,234,0.06)', width: 32, height: 32, '&:hover': { color: '#667eea' } }}
                    >
                      <ChevronLeftIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton
                      onClick={() => goToConsiglio(1)}
                      size="small"
                      sx={{ color: '#aaa', backgroundColor: 'rgba(102,126,234,0.06)', width: 32, height: 32, '&:hover': { color: '#667eea' } }}
                    >
                      <ChevronRightIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })()}

            {/* Action buttons */}
            <Box
              sx={{
                width: '100%',
                maxWidth: '400px',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mt: 1
              }}
            >
              <Button
                variant="contained"
                onClick={() => navigate('/products')}
                sx={{
                  backgroundColor: 'rgb(0, 0, 0)',
                  color: '#ffffff',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: 600,
                  borderRadius: '12px',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgb(51, 51, 51)' }
                }}
              >
                Scopri i prodotti per te
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/try-on')}
                sx={{
                  borderColor: 'rgb(0, 0, 0)',
                  color: 'rgb(0, 0, 0)',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: 600,
                  borderRadius: '12px',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'rgb(0, 0, 0)',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)'
                  }
                }}
              >
                Prova virtualmente
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default ArmocromiaResults;
